/* eslint-disable @typescript-eslint/no-explicit-any */
// IVAZDialogContext.tsx
import IconCheckWhite from '@assets/icons/check-white.svg?react';
import {
 Box,
 CircularProgress,
 Table,
 TableBody,
 TableCell,
 TableContainer,
 TableHead,
 TableRow,
} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import React, {
 createContext,
 ReactNode,
 useCallback,
 useContext,
 useEffect,
 useMemo,
 useState,
} from 'react';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import CloseButton from '../components/common/CloseButton';
import { getIVAZStart, sendIVAZStart } from '../redux/ivaz/ivazSlice';
import { RootState } from '../redux/store';

// Define types for dialog state and the context function
interface IVAZDialogProps {
 row: any;
 isOpen: boolean;
 onConfirm: () => void;
}

interface IVAZDialogContextType {
 openDialog: ({ row, onConfirm }: Omit<IVAZDialogProps, 'isOpen'>) => void;
}

// Create the context with proper types
const IVAZDialogContext = createContext<IVAZDialogContextType | undefined>(
 undefined,
);

export const IVAZDialogProvider = ({ children }: { children: ReactNode }) => {
 const dispatch = useDispatch();
 const selectorIVAZ = useSelector((state: RootState) => state.ivaz);
 const { loading, success, ivaz } = selectorIVAZ;

 const [dialogState, setDialogState] = useState<IVAZDialogProps>({
  row: undefined,
  isOpen: false,
  onConfirm: () => {},
 });
 const { row: tableRow } = dialogState;

 const openDialog = useCallback(
  ({ row, onConfirm }: Omit<IVAZDialogProps, 'isOpen'>) => {
   setDialogState({
    row,
    isOpen: true,
    onConfirm,
   });
  },
  [],
 );

 const closeDialog = useCallback(() => {
  setDialogState((prevState) => ({ ...prevState, isOpen: false }));
 }, []);

 const confirmAction = useCallback(() => {
  if (dialogState.onConfirm) dialogState.onConfirm();
  closeDialog();
 }, [dialogState.onConfirm, closeDialog]);

 // Memoize the value for the context provider
 const contextValue = useMemo(
  () => ({
   openDialog,
  }),
  [openDialog],
 );

 const {
  handleSubmit,
  control,
  formState: { errors },
  setValue,
  register,
  reset,
  watch,
 } = useForm({
  mode: 'onChange',
 });

 const submitIVAZ = (data: FieldValues) => {
  dispatch(
   sendIVAZStart({
    ArrivalTime: dayjs(data.ArrivalTime).toISOString(),
    DispatchTime: dayjs(data.DispatchTime).toISOString(),
    ResultId: data.ResultId,
    OrderId: 1,
   }),
  );
  console.log(data);
 };

 useEffect(() => {
  // if (success?.ivazsGeneratePDF) {
  //  if (ivaz.file && ivaz.type && ivaz.extension && ivaz.name) {
  //   downloadBase64File(ivaz.file, ivaz.name, ivaz.type, ivaz.extension);
  //  }
  // }
 }, [success]);

 useEffect(() => {
  if (tableRow?.Id) {
   dispatch(getIVAZStart({ resultId: tableRow?.Id }));
  }
  // if()
 }, [tableRow]);

 return (
  <IVAZDialogContext.Provider value={contextValue}>
   {children}
   {dialogState.isOpen && (
    <Dialog
     open={dialogState.isOpen}
     onClose={closeDialog}
     sx={{
      '& .MuiDialog-container': {
       '& .MuiPaper-root': {
        width: '100%',
        overflowY: 'hidden',
        //  height: '100%',
        maxWidth: '900px', // 960
       },
      },
     }}
    >
     <DialogTitle>i.VAZ</DialogTitle>
     <CloseButton closeDialog={closeDialog} />
     <DialogContent>
      <Box
       sx={{
        // display: 'flex',
        // alignItems: 'center',
        gap: 1,
        py: 2,
        width: '100%',
       }}
      >
       <ul>
        <li>
         Kur eina ArrivalTime, DispatchTime, DocumentUID, Error, Modified,
         OrderId, OrderTime, ResultTime, status, UID?{' '}
        </li>
        <li> Also, why OrderID is required when submitting? </li>
        <li>what is send and approve modal?</li>
       </ul>

       <TableContainer sx={{ mb: 3 }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
         <TableHead>
          <TableRow>
           <TableCell>Data</TableCell>
           <TableCell>Automobilis</TableCell>
           <TableCell>Vairuotojas</TableCell>
           <TableCell>Siuntėjas</TableCell>
           <TableCell>Gavėjas</TableCell>
           <TableCell>Karjeras</TableCell>
           <TableCell>Tara</TableCell>
           <TableCell>Bruto</TableCell>
          </TableRow>
         </TableHead>
         <TableBody>
          {ivaz &&
           ivaz?.map((iv) => (
            <TableRow
             key={iv.Id}
             sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
             <TableCell>{iv.ResultTime}</TableCell>
             <TableCell>{tableRow?.Car?.Number}</TableCell>
             <TableCell>{tableRow?.Driver?.FullName}</TableCell>
             <TableCell>{tableRow?.Sender?.Name}</TableCell>
             <TableCell>{tableRow?.Sender?.Receiver}</TableCell>
             <TableCell>{tableRow?.Sender?.Carrier}</TableCell>
             <TableCell>{tableRow?.Tare?.Weight}</TableCell>
             <TableCell>{tableRow?.Bruto?.Weight}</TableCell>
            </TableRow>
           ))}
         </TableBody>
        </Table>
       </TableContainer>
       <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="lt">
        <Box component="form" sx={{ display: 'flex', gap: 2 }}>
         <input
          {...register('ResultId', { required: true })}
          type="hidden"
          value={tableRow?.Id}
         />
         {[
          { name: 'ArrivalTime', label: 'Atvykimo laikas' },
          { name: 'DispatchTime', label: 'Išvykimo laikas' },
         ].map((input) => (
          <Controller
           key={`ivaz-${input.name}`}
           name={input.name}
           control={control}
           defaultValue=""
           rules={{
            required: `Required`,
           }}
           render={({ field }) => (
            <DateTimePicker
             sx={{ width: '100%' }}
             {...field}
             value={dayjs(field.value) || ''}
             label={input.label}
             slotProps={{
              textField: {
               label: input.label,
               error: !!errors?.[input.name], // Apply error styling
               helperText: errors?.[input.name]?.message?.toString(), // Show error message
               fullWidth: true,
              },
             }}
            />
           )}
          />
         ))}
        </Box>
       </LocalizationProvider>
      </Box>
     </DialogContent>
     <DialogActions>
      <Button
       onClick={handleSubmit(submitIVAZ)}
       type="button"
       size="large"
       variant="contained"
       color="info"
       disabled={loading?.sendIVAZ || false}
       endIcon={
        loading?.sendIVAZ ? (
         <CircularProgress size={20} />
        ) : (
         <IconCheckWhite style={{ width: 16, height: 11 }} />
        )
       }
      >
       Sukurti
      </Button>
     </DialogActions>
    </Dialog>
   )}
  </IVAZDialogContext.Provider>
 );
};

export const useIVAZDialog = (): IVAZDialogContextType => {
 const context = useContext(IVAZDialogContext);
 if (!context) {
  throw new Error('useIVAZDialog must be used within a IVAZDialogProvider');
 }
 return context;
};
